/**
 * Sieve — queue loading & the "today" selector (§3, §5), generic over both the
 * prime queue and the Multiples queue.
 *
 * Each queue is a static JSON asset (§5.1) fetched once and cached (the service
 * worker precaches both for offline play). Which puzzle is "today" is derived
 * from the LOCAL device date — reset at local midnight (§3).
 */

import type { Puzzle } from '../engine/grid';
import type { MultiplesPuzzle } from '../engine/grid-multiples';

/** A queue entry only needs an id and a date for the today-selector. */
interface Dated {
  id: number;
  date: string;
}

export interface Queue<P extends Dated = Dated> {
  schemaVersion: number;
  count: number;
  puzzles: P[];
}

export type PrimeQueue = Queue<Puzzle>;
export type MultiplesQueue = Queue<MultiplesPuzzle>;

const cache = new Map<string, Queue>();

export async function loadQueue<P extends Dated = Dated>(url: string): Promise<Queue<P>> {
  const hit = cache.get(url);
  if (hit) return hit as Queue<P>;
  const res = await fetch(url, { cache: 'force-cache' });
  if (!res.ok) throw new Error(`Failed to load queue ${url}: ${res.status}`);
  const q = (await res.json()) as Queue<P>;
  cache.set(url, q as Queue);
  return q;
}

export const loadPrimeQueue = () => loadQueue<Puzzle>('queue.json');
export const loadMultiplesQueue = () => loadQueue<MultiplesPuzzle>('queue-multiples.json');

/** Local date as YYYY-MM-DD (device timezone) — the daily boundary (§3). */
export function localDateString(d = new Date()): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function daysBetween(fromIso: string, toIso: string): number {
  const [fy, fm, fd] = fromIso.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = toIso.split('-').map(Number) as [number, number, number];
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
}

/**
 * Index of today's puzzle. Before the queue's start date we clamp to the first
 * puzzle; after the end, to the last — so there is always a playable "today".
 */
export function indexForDate<P extends Dated>(queue: Queue<P>, localIso: string): number {
  const start = queue.puzzles[0]?.date ?? localIso;
  const offset = daysBetween(start, localIso);
  return Math.max(0, Math.min(queue.count - 1, offset));
}

export function puzzleForToday<P extends Dated>(queue: Queue<P>, now = new Date()): P {
  return queue.puzzles[indexForDate(queue, localDateString(now))]!;
}

export function puzzleById<P extends Dated>(queue: Queue<P>, id: number): P | undefined {
  return queue.puzzles.find((p) => p.id === id);
}

/** Milliseconds until the next local midnight — drives the end-modal countdown. */
export function msUntilLocalMidnight(now = new Date()): number {
  const next = new Date(now);
  next.setHours(24, 0, 0, 0);
  return next.getTime() - now.getTime();
}

export function formatCountdown(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}
